import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';
const FADE_THRESHOLD_PX = 4;
export interface SidebarScrollState {
  scrollRef: React.RefObject<HTMLDivElement | null>;
  isPinned: boolean;
  showTopFade: boolean;
  showBottomFade: boolean;
}

/**
 * Tracks scroll state for a sidebar container.
 *
 * `isPinned` is true when the element referenced by `pinSentinelRef` is outside the scroll viewport
 * (i.e. fully scrolled past). When no sentinel is provided, `isPinned` falls back to "scrolled > 8px".
 */
export function useSidebarScrollState(options) {
  const $ = _c(9);
  const pinSentinelRef = options?.pinSentinelRef;
  const scrollRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      isPinned: false,
      showTopFade: false,
      showBottomFade: false
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [state, setState] = useState(t0);
  let t1;
  let t2;
  if ($[1] !== pinSentinelRef) {
    t1 = () => {
      const el = scrollRef.current;
      if (!el) {
        return;
      }
      const updateFades = () => {
        const {
          scrollTop,
          scrollHeight,
          clientHeight
        } = el;
        const distanceFromBottom = scrollHeight - clientHeight - scrollTop;
        setState(prev => ({
          ...prev,
          showTopFade: scrollTop > FADE_THRESHOLD_PX,
          showBottomFade: distanceFromBottom > FADE_THRESHOLD_PX,
          ...(pinSentinelRef ? {} : {
            isPinned: scrollTop > 8
          })
        }));
      };
      updateFades();
      el.addEventListener("scroll", updateFades, {
        passive: true
      });
      const ro = new ResizeObserver(updateFades);
      ro.observe(el);
      return () => {
        el.removeEventListener("scroll", updateFades);
        ro.disconnect();
      };
    };
    t2 = [pinSentinelRef];
    $[1] = pinSentinelRef;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[4] !== pinSentinelRef) {
    t3 = () => {
      if (!pinSentinelRef) {
        return;
      }
      const sentinel = pinSentinelRef.current;
      const root = scrollRef.current;
      if (!sentinel || !root) {
        return;
      }
      const io = new IntersectionObserver(entries => {
        const entry = entries[0];
        if (!entry) {
          return;
        }
        setState(prev_0 => ({
          ...prev_0,
          isPinned: !entry.isIntersecting
        }));
      }, {
        root,
        threshold: 0
      });
      io.observe(sentinel);
      return () => io.disconnect();
    };
    t4 = [pinSentinelRef];
    $[4] = pinSentinelRef;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] !== state) {
    t5 = {
      scrollRef,
      ...state
    };
    $[7] = state;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}